import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";
import { withShioriAccessContext } from "@/api/shiori-access-context.js";

const normalizeScore = (value) => {
  const numberValue = Number(value);
  return Number.isInteger(numberValue) && numberValue >= 1 && numberValue <= 5
    ? numberValue
    : null;
};

const readValue = (source, camelKey, legacyKey) => {
  if (!source) return null;
  const snakeKey = camelKey.replace(/[A-Z]/g, (letter) => `_${letter}`).toLowerCase();
  const capsKey = snakeKey.toUpperCase();
  const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
  return (
    source[camelKey] ??
    source[pascalKey] ??
    source[snakeKey] ??
    source[legacyKey] ??
    source[capsKey] ??
    null
  );
};

const normalizeReview = (review) => {
  if (!review) return null;

  return {
    ...review,
    lodgingScore: normalizeScore(readValue(review, "lodgingScore", "LODGING_SCORE")),
    foodScore: normalizeScore(readValue(review, "foodScore", "FOOD_SCORE")),
    transportScore: normalizeScore(
      readValue(review, "transportScore", "TRANSPORT_SCORE"),
    ),
    sightseeingScore: normalizeScore(
      readValue(review, "sightseeingScore", "SIGHTSEEING_SCORE"),
    ),
    activityScore: normalizeScore(
      readValue(review, "activityScore", "ACTIVITY_SCORE"),
    ),
    freeText: readValue(review, "freeText", "FREE_TEXT") || "",
    aiUsageConsent: Boolean(
      review.aiUsageConsent ?? review.AI_USAGE_CONSENT ?? false,
    ),
  };
};

const lodgingScoreKeys = [
  "cleanlinessScore",
  "facilitiesScore",
  "serviceScore",
  "locationScore",
  "valueScore",
];

const normalizeLodgingReview = (review) => {
  if (!review) return null;

  return {
    ...review,
    lodgingReviewId: readValue(review, "lodgingReviewId", "LODGING_REVIEW_ID"),
    reviewCategory: readValue(review, "reviewCategory", "REVIEW_CATEGORY") || "lodging",
    lodgingKey: readValue(review, "lodgingKey", "LODGING_KEY"),
    sourceLineNo: readValue(review, "sourceLineNo", "SOURCE_LINE_NON"),
    lodgingName: readValue(review, "lodgingName", "LODGING_NAME") || "",
    itemName:
      readValue(review, "itemName", "LODGING_NAME") ||
      readValue(review, "lodgingName", "LODGING_NAME") ||
      "",
    address: readValue(review, "address", "ADDRESS_NMV") || "",
    startDate: readValue(review, "startDate", "START_YMD"),
    url: readValue(review, "url", "URL_NMV") || "",
    cleanlinessScore: normalizeScore(
      readValue(review, "cleanlinessScore", "CLEANLINESS_SCORE"),
    ),
    facilitiesScore: normalizeScore(
      readValue(review, "facilitiesScore", "FACILITIES_SCORE"),
    ),
    serviceScore: normalizeScore(readValue(review, "serviceScore", "SERVICE_SCORE")),
    locationScore: normalizeScore(
      readValue(review, "locationScore", "LOCATION_SCORE"),
    ),
    valueScore: normalizeScore(readValue(review, "valueScore", "VALUE_SCORE")),
  };
};

const normalizeLodgingReviews = (reviews) =>
  Array.isArray(reviews) ? reviews.map(normalizeLodgingReview).filter(Boolean) : [];

const buildLodgingReviewPayload = (reviews) =>
  normalizeLodgingReviews(reviews)
    .filter((review) => review.lodgingKey)
    .map((review) => {
      const payload = {
        reviewCategory: review.reviewCategory || "lodging",
        lodgingKey: review.lodgingKey,
      };
      lodgingScoreKeys.forEach((key) => {
        payload[key] = normalizeScore(review[key]);
      });
      return payload;
    });

const normalizePrompt = (prompt) => ({
  ...prompt,
  shioriId: prompt.shioriId || prompt.SHIORI_NOV || prompt.shiori_id,
  title: prompt.title || prompt.TITLE || prompt.SUBJECT_NMV || "旅行",
  tripFrom: prompt.tripFrom || prompt.TRIP_FROM || null,
  tripTo: prompt.tripTo || prompt.TRIP_TO || null,
  hasReview: Boolean(prompt.hasReview ?? prompt.HAS_REVIEW ?? false),
  dismissed: Boolean(prompt.dismissed ?? prompt.DISMISSED ?? false),
  snoozeUntil: prompt.snoozeUntil || prompt.SNOOZE_UNTIL || null,
});

const normalizePromptResponse = (data) => {
  const prompts = Array.isArray(data) ? data : data?.prompts;
  return Array.isArray(prompts) ? prompts.map(normalizePrompt) : [];
};

const normalizeSummary = (summary) => {
  const score = Number(
    readValue(summary, "overallScore", "overallScore") ??
      summary?.OVERALL_SCORE,
  );
  return {
    shioriId: readValue(summary, "shioriId", "SHIORI_NOV"),
    overallScore: Number.isFinite(score) ? score : null,
    scoredCategoryCount:
      Number(readValue(summary, "scoredCategoryCount", "scoredCategoryCount")) || 0,
  };
};

const normalizeSummaryResponse = (data) => {
  const summaries = Array.isArray(data) ? data : data?.summaries;
  return Array.isArray(summaries)
    ? summaries.map(normalizeSummary).filter((summary) => summary.shioriId)
    : [];
};

const buildPayload = (payload) => ({
  lodgingScore: normalizeScore(payload.lodgingScore),
  foodScore: normalizeScore(payload.foodScore),
  transportScore: normalizeScore(payload.transportScore),
  sightseeingScore: normalizeScore(payload.sightseeingScore),
  activityScore: normalizeScore(payload.activityScore),
  freeText: payload.freeText?.trim() || null,
  aiUsageConsent: Boolean(payload.aiUsageConsent),
  lodgingReviews: buildLodgingReviewPayload(payload.lodgingReviews),
  itemReviews: buildLodgingReviewPayload(payload.itemReviews),
});

export const useTripReviewStore = defineStore("tripReview", {
  state: () => ({
    currentReview: null,
    currentShioriId: null,
    lodgingReviews: [],
    itemReviews: [],
    promptCandidates: [],
    reviewSummaries: {},
    loading: false,
    saving: false,
    error: null,
  }),

  actions: {
    async fetchReview(shioriId) {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(`${API_URL}/trip-reviews/${shioriId}`);
        const review = response.data?.review ?? response.data ?? null;
        const itemReviews = response.data?.itemReviews ?? response.data?.lodgingReviews ?? [];
        const lodgingReviews = response.data?.lodgingReviews ?? itemReviews.filter((item) => (item.reviewCategory || item.REVIEW_CATEGORY || "lodging") === "lodging");
        this.currentReview = normalizeReview(review);
        this.currentShioriId = shioriId;
        this.itemReviews = normalizeLodgingReviews(itemReviews);
        this.lodgingReviews = normalizeLodgingReviews(lodgingReviews);
        return this.currentReview;
      } catch (err) {
        if (err.response?.status === 404) {
          this.currentReview = null;
          this.currentShioriId = shioriId;
          this.lodgingReviews = [];
          this.itemReviews = [];
          return null;
        }
        this.error =
          err.response?.data?.detail || "振り返りの読み込みに失敗しました";
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async saveReview(shioriId, payload) {
      this.saving = true;
      this.error = null;
      try {
        const response = await axios.put(
          `${API_URL}/trip-reviews/${shioriId}`,
          buildPayload(payload),
          withShioriAccessContext(shioriId),
        );
        const review = response.data?.review ?? response.data ?? null;
        const nextPayload = buildPayload(payload);
        this.currentReview = normalizeReview(review) || nextPayload;
        this.currentShioriId = shioriId;
        this.itemReviews = normalizeLodgingReviews(
          response.data?.itemReviews ?? payload.itemReviews ?? payload.lodgingReviews,
        );
        this.lodgingReviews = normalizeLodgingReviews(
          response.data?.lodgingReviews ??
            this.itemReviews.filter((item) => item.reviewCategory === "lodging"),
        );
        this.promptCandidates = this.promptCandidates.filter(
          (prompt) => prompt.shioriId !== shioriId,
        );
        return this.currentReview;
      } catch (err) {
        this.error = err.response?.data?.detail || "振り返りの保存に失敗しました";
        throw err;
      } finally {
        this.saving = false;
      }
    },

    async fetchReviewPrompts() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(`${API_URL}/trip-reviews/prompts`);
        this.promptCandidates = normalizePromptResponse(response.data);
        return this.promptCandidates;
      } catch (err) {
        this.error =
          err.response?.data?.detail || "振り返り候補の取得に失敗しました";
        this.promptCandidates = [];
        return [];
      } finally {
        this.loading = false;
      }
    },

    async fetchReviewSummaries() {
      this.error = null;
      try {
        const response = await axios.get(`${API_URL}/trip-reviews/summaries`);
        const summaries = normalizeSummaryResponse(response.data);
        this.reviewSummaries = summaries.reduce((acc, summary) => {
          acc[summary.shioriId] = summary;
          return acc;
        }, {});
        return summaries;
      } catch (err) {
        this.error =
          err.response?.data?.detail || "謖ｯ繧願ｿ斐ｊ蛟､縺ｮ蜿門ｾ励↓螟ｱ謨励＠縺ｾ縺励◆";
        this.reviewSummaries = {};
        return [];
      }
    },

    async updatePromptState(shioriId, actionPayload) {
      this.error = null;
      try {
        const response = await axios.post(
          `${API_URL}/trip-reviews/${shioriId}/prompt-state`,
          actionPayload,
          withShioriAccessContext(shioriId),
        );
        this.promptCandidates = this.promptCandidates.filter(
          (prompt) => prompt.shioriId !== shioriId,
        );
        return response.data;
      } catch (err) {
        this.error =
          err.response?.data?.detail || "振り返り通知の更新に失敗しました";
        throw err;
      }
    },

    resetCurrentReview() {
      this.currentReview = null;
      this.currentShioriId = null;
      this.lodgingReviews = [];
      this.itemReviews = [];
    },
    clearShioriReviewData(shioriId) {
      if (String(this.currentShioriId) === String(shioriId)) {
        this.resetCurrentReview();
      }
      this.promptCandidates = this.promptCandidates.filter(
        (prompt) => String(prompt.shioriId) !== String(shioriId),
      );
      const nextSummaries = { ...this.reviewSummaries };
      delete nextSummaries[shioriId];
      this.reviewSummaries = nextSummaries;
      this.error = null;
    },
  },
});
